import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';

/**
 *  Tanévváltó / tanév-előkészítő.
 *
 *  Állapotgép: előkészítés → aktív → lezárt.
 *   - Új tanév létrehozása előkészítés státusszal: az adatok már tölthetők
 *     (osztályok léptetése, törzsadat, csoportok, TTF), de értékelés nem írható rá,
 *     és a régi tanév felületein nem jelenik meg.
 *   - Megnyitás: ez lesz az intézmény aktuális tanéve, az eddigi automatikusan lezárul.
 *   - Lezárt tanévbe csak admin jogkörrel lehet utólag rögzíteni (backend őr).
 */
@Component({
  selector: 'app-tanevvalto',
  templateUrl: './tanevvalto.component.html',
  styleUrls: ['./tanevvalto.component.scss'],
})
export class TanevvaltoComponent implements OnInit {
  sajatIntezmeny: any;
  jogosult = false;
  betoltes = false;

  aktualistanev: number | null = null;
  // Az intézmény ELSŐ tanéve (feladatellatas.elsotanev). Eddig csak a Törzsadat-
  // szerkesztő „Feladatellátási helyek" tábláján, nyers mezőként lehetett állítani –
  // most itt van, a tanévekkel együtt.
  elsotanev: number | null = null;
  elsotanevMentes = false;
  munkatanev: number | null = null;
  tanevek: any[] = [];

  // Új tanév űrlap
  ujForm = false;
  uj: { id: number | null; name: string; tankezd: string; felev: string; tanvege: string } = {
    id: null, name: '', tankezd: '', felev: '', tanvege: '',
  };
  ujFolyamatban = false;

  // Előkészítés (léptetési terv)
  elokeszitendo: any = null;      // a cél tanév sora
  forrasEv: number | null = null;
  terv: any = null;               // { osztalyok: [...], masolhato: {...} }
  // MINDEN átemelhető elem alapból be van pipálva: a csoport és a tantárgyfelosztás
  // korábban alapból KI volt kapcsolva, ezért az előkészítés „nem léptette a csoportokat".
  opciok: any = {
    tantargy: true, kerettanterv: true, terem: true, csengetes: true,
    csoport: true, ttf: true,
    /*  Alapból KI: az átvétel csak hozzáad. A tükrözés töröl is, ezért tudatos
     *  döntésnek kell lennie – nem szabad, hogy egy megszokott gombnyomás vigye el
     *  a célévben kézzel felvett sorokat. Egy kapcsoló, mert a kérdés is egy: a
     *  forrás legyen-e a mérvadó. */
    tukrozes: false,
  };
  tervBetolt = false;
  elokeszitesFut = false;
  eredmeny: any = null;

  private readonly statuszCimke: { [k: string]: string } = {
    elokeszites: 'Előkészítés alatt',
    aktiv: 'Aktív',
    lezart: 'Lezárt',
  };
  private readonly statuszSzin: { [k: string]: string } = {
    elokeszites: 'warning', aktiv: 'success', lezart: 'medium',
  };

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    const jogkor = await this.data.getJogkor();
    // A hozzáférés a Jogosultságok szerkesztőből jön, nem az 'admin' jogkör nevéből –
    // így más jogkör is megkaphatja ezt a képernyőt (/admin/tanevvalto).
    this.jogosult = (await this.data.hozzaferesek()).szabad('/admin/tanevvalto');
    this.munkatanev = await this.data.getMunkatanev();
    this.betolt();
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  /** Bármilyen dátum-alak → 'YYYY-MM-DD' (a dátummező csak ezt fogadja el). */
  private nap(v: any): string {
    if (!v) return '';
    const s = String(v);
    return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  }

  /** Van-e egyáltalán előkészítés alatt álló tanév (az Előkészítés gomb csak ilyennél jelenik meg). */
  get vanElokeszites(): boolean {
    return this.tanevek.some((t) => t.statusz === 'elokeszites');
  }

  statuszNev(s: string) { return this.statuszCimke[s] || s || '—'; }
  statuszSzine(s: string) { return this.statuszSzin[s] || 'medium'; }

  betolt() {
    if (!this.intezmenyId) return;
    this.betoltes = true;
    this.http.get<any>('/api/tanev/lista/' + this.intezmenyId).subscribe({
      next: (r) => {
        this.betoltes = false;
        this.aktualistanev = r?.aktualistanev ?? null;
        this.elsotanev = r?.elsotanev ?? null;
        // A dátummezők csak 'YYYY-MM-DD' alakot tudnak megjeleníteni (a backend már így
        // adja vissza, de régebbi/ISO formátumot is elviselünk).
        this.tanevek = (Array.isArray(r?.tanevek) ? r.tanevek : []).map((t: any) => ({
          ...t,
          tankezd: this.nap(t.tankezd),
          felev: this.nap(t.felev),
          tanvege: this.nap(t.tanvege),
        }));
        if (this.munkatanev == null) this.munkatanev = this.aktualistanev;
        this.ujFormElokeszit();
      },
      error: (e) => {
        this.betoltes = false;
        this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a tanéveket.', 'error');
      },
    });
  }

  /** Az új tanév űrlap előtöltése az utolsó tanév alapján (évszám + dátumok +1 év). */
  ujFormElokeszit() {
    const utolso = this.tanevek.length ? this.tanevek[this.tanevek.length - 1] : null;
    const ev = utolso ? Number(utolso.id) + 1 : new Date().getFullYear();
    const evvel = (d: any, plusz: number) => {
      if (!d) return '';
      const x = new Date(d);
      x.setFullYear(x.getFullYear() + plusz);
      return x.toISOString().slice(0, 10);
    };
    this.uj = {
      id: ev,
      name: `${ev} / ${ev + 1}`,
      tankezd: utolso ? evvel(utolso.tankezd, 1) : `${ev}-09-01`,
      felev: utolso ? evvel(utolso.felev, 1) : `${ev + 1}-01-31`,
      tanvege: utolso ? evvel(utolso.tanvege, 1) : `${ev + 1}-06-15`,
    };
  }

  /**
   *  A `tanevadatok` tároló frissítése: ebből építkezik a többi felület tanév-választója
   *  (pl. a TTF), és bootstrapkor csak egyszer töltődik be – új tanév után frissíteni kell.
   */
  private frissitTanevTarolo() {
    this.http.get<any[]>('/api/nyilvantartas/tanevek/' + this.intezmenyId).subscribe({
      next: async (lista) => { await this.data.setStorage('tanevadatok', JSON.stringify(lista || [])); },
      error: () => { /* a következő bejelentkezéskor úgyis frissül */ },
    });
  }

  ujTanev() {
    if (!this.uj.id) { this.data.sendToast('Adj meg évszámot.', 'warning'); return; }
    this.ujFolyamatban = true;
    this.http.post<any>('/api/tanev/uj/' + this.intezmenyId, this.uj).subscribe({
      next: () => {
        this.ujFolyamatban = false;
        this.ujForm = false;
        this.data.sendToast('A tanév létrejött, előkészítés státusszal.');
        this.frissitTanevTarolo();
        this.betolt();
      },
      error: (e) => {
        this.ujFolyamatban = false;
        this.data.sendToast(e?.error?.error || 'Nem sikerült létrehozni a tanévet.', 'error');
      },
    });
  }

  mentElsotanev() {
    this.elsotanevMentes = true;
    this.http.patch<any>(`/api/tanev/intezmeny/${this.intezmenyId}`, { elsotanev: this.elsotanev })
      .subscribe({
        next: () => { this.elsotanevMentes = false; this.data.sendToast('Első tanév mentve.'); },
        error: (e) => {
          this.elsotanevMentes = false;
          this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
        },
      });
  }

  mentDatumok(t: any) {
    this.http.patch<any>(`/api/tanev/${t.id}/${this.intezmenyId}`, {
      name: t.name,
      tankezd: String(t.tankezd || '').slice(0, 10),
      felev: String(t.felev || '').slice(0, 10),
      tanvege: String(t.tanvege || '').slice(0, 10),
    }).subscribe({
      next: () => this.data.sendToast('Tanév adatai mentve.'),
      error: (e) => this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error'),
    });
  }

  /* ── Előkészítés ── */

  elokeszitesNyit(t: any) {
    this.elokeszitendo = t;
    this.eredmeny = null;
    this.forrasEv = this.aktualistanev ?? (Number(t.id) - 1);
    this.tervBetoltes();
  }

  tervBetoltes() {
    if (!this.elokeszitendo) return;
    this.tervBetolt = true;
    this.terv = null;
    const qs = this.forrasEv ? `?forras=${this.forrasEv}` : '';
    this.http.get<any>(`/api/tanev/elokeszites/${this.elokeszitendo.id}/${this.intezmenyId}${qs}`).subscribe({
      next: (r) => {
        this.tervBetolt = false;
        this.terv = r;
        this.forrasEv = r?.forras_ev ?? this.forrasEv;
      },
      error: (e) => {
        this.tervBetolt = false;
        this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni a léptetési tervet.', 'error');
      },
    });
  }

  mindLeptet(ertek: boolean) {
    for (const o of (this.terv?.osztalyok || [])) this.leptetValt(o, ertek);
  }

  /**
   *  A léptetés és a kiléptetés kizárja egymást: ha az osztály tovább lép, a tanulói
   *  jogviszony az új osztályon folytatódik; ha nem lép, akkor zárható le (végzősök).
   */
  leptetValt(o: any, ertek: boolean) {
    o.leptet = ertek;
    if (ertek) { o.kileptet = false; }
    else { o.tanulok = false; }
  }

  /** Hány tanuló jogviszonya zárulna le a kiléptetéssel. */
  get kileptetendoDb(): number {
    return (this.terv?.osztalyok || [])
      .filter((o: any) => o.kileptet && !o.leptet)
      .reduce((s: number, o: any) => s + (Number(o.tanulo_db) || 0), 0);
  }

  elokeszitesFuttat() {
    if (!this.elokeszitendo) return;
    const leptetendo = (this.terv?.osztalyok || []).filter((o: any) => o.leptet).length;
    const uzenet = `Biztosan futtatod az előkészítést?\n\n`
      + `Forrás: ${this.forrasEv} → cél: ${this.elokeszitendo.id}\n`
      + `${leptetendo} osztály lép tovább.\n`
      + (this.kileptetendoDb
          ? `${this.kileptetendoDb} végzős tanuló jogviszonya lezárul a forrás tanév végével.\n` : '')
      + `\nAz előző tanév adatai változatlanok maradnak (új osztály + új besorolás jön létre).\n`
      + `A művelet többször is futtatható – a már létező adatokat nem duplikálja.`;
    if (!confirm(uzenet)) return;

    this.elokeszitesFut = true;
    this.eredmeny = null;
    this.http.post<any>(`/api/tanev/elokeszites/${this.elokeszitendo.id}/${this.intezmenyId}`, {
      forras: this.forrasEv,
      opciok: this.opciok,
      osztalyok: this.terv?.osztalyok || [],
    }).subscribe({
      next: (r) => {
        this.elokeszitesFut = false;
        this.eredmeny = r?.eredmeny || {};
        this.data.sendToast('Az előkészítés lefutott.');
        this.betolt();
        this.tervBetoltes();
      },
      error: (e) => {
        this.elokeszitesFut = false;
        this.data.sendToast(e?.error?.error || 'Az előkészítés nem sikerült.', 'error');
      },
    });
  }

  /* ── Állapotváltások ── */

  megnyitas(t: any) {
    const uzenet = `Megnyitod a(z) ${t.name} tanévet?\n\n`
      + (this.aktualistanev ? `A(z) ${this.aktualistanev} tanév ezzel LEZÁRUL (utólag csak admin írhat bele).\n` : '')
      + `Ettől kezdve a napló, az értékelés és a hiányzás az új tanévre megy.`;
    if (!confirm(uzenet)) return;

    this.http.post<any>(`/api/tanev/megnyitas/${t.id}/${this.intezmenyId}`, {}).subscribe({
      next: async () => {
        this.data.sendToast(`A(z) ${t.name} tanév megnyitva.`);
        // A tárolt tanév-adatok frissítése, hogy a felület azonnal az új évre álljon.
        await this.data.setMunkatanev(Number(t.id));
        this.munkatanev = Number(t.id);
        try {
          const tarolt = JSON.parse(await this.data.getFromStorage('tanev')) || {};
          tarolt.aktualistanev = Number(t.id);
          await this.data.setStorage('tanev', JSON.stringify(tarolt));
        } catch (e) { /* a következő bejelentkezéskor úgyis frissül */ }
        this.frissitTanevTarolo();
        this.betolt();
      },
      error: (e) => this.data.sendToast(e?.error?.error || 'A megnyitás nem sikerült.', 'error'),
    });
  }

  lezaras(t: any) {
    if (!confirm(`Lezárod a(z) ${t.name} tanévet? Utána csak admin jogkörrel írható.`)) return;
    this.http.post<any>(`/api/tanev/lezaras/${t.id}/${this.intezmenyId}`, {}).subscribe({
      next: () => { this.data.sendToast('A tanév lezárva.'); this.betolt(); },
      error: (e) => this.data.sendToast(e?.error?.error || 'A lezárás nem sikerült.', 'error'),
    });
  }

  ujranyitas(t: any) {
    if (!confirm(`Újranyitod a(z) ${t.name} tanévet? Ezzel újra írhatóvá válik.`)) return;
    this.http.post<any>(`/api/tanev/ujranyitas/${t.id}/${this.intezmenyId}`, {}).subscribe({
      next: () => { this.data.sendToast('A tanév újranyitva.'); this.betolt(); },
      error: (e) => this.data.sendToast(e?.error?.error || 'Az újranyitás nem sikerült.', 'error'),
    });
  }

  /**
   *  Vissza előkészítés státuszba: ha egy tanév tévedésből aktív/lezárt lett, ezzel
   *  válik újra elérhetővé rajta az Előkészítés (osztályléptetés) és az adatostul törlés.
   */
  elokeszitesbe(t: any) {
    if (!confirm(`A(z) ${t.name} tanév visszakerül előkészítés státuszba.\n\n`
      + `Ezután újra elérhető rajta az Előkészítés (osztályok léptetése), és `
      + `szükség esetén az adataival együtt törölhető.`)) return;
    this.http.post<any>(`/api/tanev/elokeszitesbe/${t.id}/${this.intezmenyId}`, {}).subscribe({
      next: () => { this.data.sendToast('A tanév előkészítés státuszba került.'); this.betolt(); },
      error: (e) => this.data.sendToast(e?.error?.error || 'A státusz módosítása nem sikerült.', 'error'),
    });
  }

  torles(t: any) {
    const elokeszites = t.statusz === 'elokeszites';
    const adatok = t.szamlalok || {};
    const vanAdat = (adatok.osztaly || 0) + (adatok.kerettanterv || 0) + (adatok.csoport || 0)
      + (adatok.ttf || 0) + (adatok.tantargy || 0) + (adatok.terem || 0) + (adatok.csengetes || 0) > 0;

    let torolAdatok = false;
    if (elokeszites && vanAdat) {
      // Előkészítés alatt álló tanév: az előkészítő adatok is törölhetők vele együtt.
      torolAdatok = confirm(
        `Törlöd a(z) ${t.name} előkészítés alatt álló tanévet?\n\n`
        + `OK = az előkészített adatok IS törlődnek `
        + `(osztály ${adatok.osztaly || 0}, tanulói besorolás, kerettanterv ${adatok.kerettanterv || 0}, `
        + `csoport ${adatok.csoport || 0}, TTF ${adatok.ttf || 0}, terem ${adatok.terem || 0}).\n`
        + `Mégse = csak a tanév-bejegyzés törlődjön, az adatok maradjanak.`);
      if (!torolAdatok && !confirm(
        `Csak a tanév-bejegyzést törlöd, az adatok maradnak. Folytatod?`)) return;
    } else if (!confirm(`Biztosan törlöd a(z) ${t.name} tanévet?`)) {
      return;
    }

    const qs = torolAdatok ? '?torolAdatok=1' : '';
    this.http.delete<any>(`/api/tanev/${t.id}/${this.intezmenyId}${qs}`).subscribe({
      next: () => {
        this.data.sendToast('A tanév törölve.');
        if (this.elokeszitendo?.id === t.id) { this.elokeszitendo = null; this.terv = null; }
        this.frissitTanevTarolo();
        this.betolt();
      },
      error: (e) => {
        // A backend elmondja, miért nem törölhető (pl. már van hozzá adat).
        const sz = e?.error?.szamok;
        const reszlet = sz
          ? ` (osztály ${sz.osztaly}, tanulói besorolás ${sz.tanuloi_besorolas}, `
            + `kerettanterv ${sz.kerettanterv}, tantárgy ${sz.tantargy}, csoport ${sz.csoport}, `
            + `TTF ${sz.ttf}, terem ${sz.terem}, csengetés ${sz.csengetes})`
          : '';
        this.data.sendToast((e?.error?.error || 'A törlés nem sikerült.') + reszlet, 'error');
      },
    });
  }

  /* ── Munkatanév ── */

  async munkatanevValt(ev: any) {
    const szam = ev == null ? null : Number(ev);
    await this.data.setMunkatanev(szam);
    this.munkatanev = szam;
    this.data.sendToast(
      szam === this.aktualistanev
        ? 'A munkatanév az aktuális tanévre állt.'
        : `Munkatanév: ${szam}. A törzsadat-felületek (osztályok, csoportok, TTF) erre az évre dolgoznak.`);
  }
}
